import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DollarSign, FileText, Upload, Printer, Trash2,
  Ship, MapPin, Package, Calendar, User, Filter,
  ChevronRight, Eye, Clock, CheckCircle2, AlertCircle,
  ArrowUpDown, ArrowDownZA, ArrowUpAZ, Mail,
  TrendingUp, Sparkles, ArrowUpRight, ArrowDownRight,
  Globe, XCircle, Brain, RotateCcw, Plus,
  List, LayoutGrid, BookmarkPlus, Activity,
} from 'lucide-react';

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  getImportRateQuoteList, deleteImportRateQuoteList,
  getCancelledImportRateQuoteList, reinstateImportRateQuoteList,
  getImportRateQuoteByID,
  insertRateQuoteImport,
} from '@/redux/ImportRateQuote/actions';
import moment from 'moment';
import {
  DropdownMenu, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuPortal, DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import SearchInputWithClear from '@/components/ui/SearchInputWithClear';
import FileUpload from '@/components/FileUpload/FileUpload';
import RateQuoteNote from './RateQuote/RateQuoteNote';
import RateQuoteCancelModal from '@/components/Modal/RateQuoteCancelModal';
import RateQuoteCancelRestoreModal from '@/components/Modal/RateQuoteCancelRestoreModal';
import { showToast } from '@/redux/Toast/actions';
import PortBadge from '../RateQuote/PortBadge';
import AIInsightCard from '@/components/shipment/AIInsightCard';
import QuoteCard from '@/components/RateQuote/quoteCard';
import { callLoader } from '@/redux/Loader/actions';

// ─── Animated typewriter title ────────────────────────────────────────────────
function AnimatedTitle() {
  const { t } = useTranslation();
  const TITLE = t('oceanImport.importRateQuote.list.title');
  return (
    <motion.h1
      initial={{ opacity: 0, y: 8, filter: 'blur(6px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="text-[18px] sm:text-[20px] font-bold tracking-tight leading-tight
                 text-gray-900 dark:text-white"
    >
      <motion.span
        className="inline-block overflow-hidden whitespace-nowrap align-bottom"
        initial={{ width: '0ch' }}
        animate={{ width: ['0ch', `${TITLE.length}ch`, `${TITLE.length}ch`, '0ch'] }}
        transition={{
          duration: 4.8,
          times: [0, 0.22, 0.9, 1],
          ease: ['linear', 'linear', 'easeInOut'],
          repeat: Infinity,
        }}
      >
        {TITLE}
      </motion.span>
    </motion.h1>
  );
}

// ─── View Toggle ──────────────────────────────────────────────────────────────
function ViewToggle({ view, setView }) {
  const { t } = useTranslation();
  const base = 'w-9 h-9 flex items-center justify-center rounded-md transition-colors';
  const active = 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400';
  const inactive = 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100';

  return (
    <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700
                    bg-white dark:bg-gray-900 p-0.5">
      <button onClick={() => setView('table')} title={t('oceanImport.common.listView')}
        className={`${base} ${view === 'table' ? active : inactive}`}>
        <List className="w-4 h-4" />
      </button>
      <button onClick={() => setView('card')} title={t('oceanImport.common.gridView')}
        className={`${base} ${view === 'card' ? active : inactive}`}>
        <LayoutGrid className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800',
    expired: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
    pending: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800',
  };
  const icons = {
    active: <CheckCircle2 className="w-3.5 h-3.5" />,
    expired: <AlertCircle className="w-3.5 h-3.5" />,
    pending: <Clock className="w-3.5 h-3.5" />,
  };
  const s = status?.toLowerCase() || 'pending';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border ${styles[s] || styles.pending}`}>
      {icons[s] || icons.pending}
      {s.charAt(0).toUpperCase() + s.slice(1)}
    </span>
  );
};

// ─── Action Button ────────────────────────────────────────────────────────────
const ActionButton = ({ icon: Icon, label, onClick, variant = 'default', id = null }) => {
  const variants = {
    default: 'bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-400',
    primary: 'bg-blue-100 hover:bg-blue-200 text-blue-600 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-400',
    warning: 'bg-amber-100 hover:bg-amber-200 text-amber-600 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 dark:text-amber-400',
    danger: 'bg-red-100 hover:bg-red-200 text-red-600 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-400',
  };

  if (label === 'Print') {
    return (
      <a href={`/import-rate-quote/print/${btoa(id)}`} target="_blank" rel="noopener noreferrer"
        className={`p-2 rounded-lg transition-all duration-200 ${variants[variant]}`} title={label}>
        <Icon className="w-4 h-4" />
      </a>
    );
  }
  return (
    <button onClick={onClick}
      className={`p-2 rounded-lg transition-all duration-200 ${variants[variant]}`} title={label}>
      <Icon className="w-4 h-4" />
    </button>
  );
};

// ─── Quote Card ───────────────────────────────────────────────────────────────


// ─── AI Insights Modal ────────────────────────────────────────────────────────
const AIInsightsModal = ({ onClose = () => { }, insightCards = [] }) => {
  const { t } = useTranslation();
  const statThemes = [
    { bg: 'bg-blue-50 dark:bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', sub: 'text-blue-600/70 dark:text-blue-400/70' },
    { bg: 'bg-emerald-50 dark:bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', sub: 'text-emerald-600/70 dark:text-emerald-400/70' },
    { bg: 'bg-red-50 dark:bg-red-500/10', text: 'text-red-600 dark:text-red-400', sub: 'text-red-600/70 dark:text-red-400/70' },
    { bg: 'bg-purple-50 dark:bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', sub: 'text-purple-600/70 dark:text-purple-400/70' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl
                   w-full max-w-3xl max-h-[80vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{t('aiQuoteAnalytics')}</h2>
                <p className="text-violet-100 text-sm">{t('aiQuoteAnalyticsDescription')}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <XCircle className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-4 gap-4 mb-6">
            {insightCards.map((card, i) => (
              <div key={card.title} className={`${statThemes[i]?.bg} rounded-xl p-4 text-center`}>
                <p className={`text-2xl font-bold ${statThemes[i]?.text}`}>{card.value}</p>
                <p className={`text-xs font-medium ${statThemes[i]?.sub}`}>{card.title}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-violet-50 to-pink-50 dark:from-violet-500/10 dark:to-pink-500/10 rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-violet-600 dark:text-violet-400" />
              <h3 className="font-bold text-slate-800 dark:text-white">{t('aiPredictions')}</h3>
            </div>
            <div className="space-y-3">
              {[
                { icon: CheckCircle2, color: 'bg-emerald-500', title: t('highConversionPotential'), desc: t('highConversionPotentialDescription') },
                { icon: AlertCircle, color: 'bg-amber-500', title: t('expiringSoon'), desc: t('expiringSoonDescription') },
                { icon: TrendingUp, color: 'bg-blue-500', title: t('marketTrend'), desc: t('marketTrendDescription') },
              ].map(({ icon: Icon, color, title, desc }) => (
                <div key={title} className="flex items-start gap-3">
                  <div className={`w-6 h-6 ${color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <Icon className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">{title}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                <h3 className="font-bold text-slate-800 dark:text-white">{t('topRoutes')}</h3>
              </div>
              <div className="space-y-3">
                {[
                  { from: 'SAVANNAH, GA', to: 'DUBAI', count: 12 },
                  { from: 'NEW YORK, NY', to: 'ROTTERDAM', count: 8 },
                  { from: 'LOS ANGELES', to: 'SHANGHAI', count: 6 },
                ].map((route, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-6 h-6 bg-emerald-100 dark:bg-emerald-500/20 rounded-full flex items-center justify-center text-xs font-bold text-emerald-600 dark:text-emerald-400">{i + 1}</span>
                    <span className="text-xs text-slate-700 dark:text-slate-200 truncate">{route.from}</span>
                    <span className="text-slate-400">→</span>
                    <span className="text-xs text-slate-700 dark:text-slate-200 truncate">{route.to}</span>
                    <span className="ml-auto text-xs font-bold text-slate-600 dark:text-slate-300">{route.count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <User className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                <h3 className="font-bold text-slate-800 dark:text-white">{t('topCustomers')}</h3>
              </div>
              <div className="space-y-3">
                {[
                  { name: 'CLEAVER BROOKS CO.', quotes: 8 },
                  { name: 'FAISAL TRADING', quotes: 5 },
                  { name: 'SAKHAMET LTD', quotes: 4 },
                ].map((customer, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-blue-100 dark:bg-blue-500/20 rounded-full flex items-center justify-center text-xs font-bold text-blue-600 dark:text-blue-400">{i + 1}</span>
                      <span className="text-xs text-slate-700 dark:text-slate-200">{customer.name}</span>
                    </div>
                    <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{customer.quotes}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-5 text-white">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5" />
              <h3 className="font-bold">{t('aiRecommendations')}</h3>
            </div>
            <ul className="space-y-2 text-sm text-blue-100">
              <li className="flex items-start gap-2"><span className="text-blue-300">•</span>{t('followUpRecommendation')}</li>
              <li className="flex items-start gap-2"><span className="text-blue-300">•</span>{t('volumeDiscountRecommendation')}</li>
              <li className="flex items-start gap-2"><span className="text-blue-300">•</span>{t('renewedQuotesRecommendation')}</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-800 px-6 py-4 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between">
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('lastUpdatedJustNow')}</p>
          <button onClick={onClose} className="px-4 py-2 bg-slate-800 dark:bg-slate-600 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors">
            {t('close')}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ImportRateQuoteList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { rateQuoteList, pagination, summary } = useSelector((s) => s.importRateQuoteReducer);
  const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? '[{}]');
  const authName = authUser?.username;
  const companyId = authUser?.companyId;

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('recent');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [fileUploadVisible, setFileUploadVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showRateQuoteNote, setShowRateQuoteNote] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteRestoreModal, setShowDeleteRestoreModal] = useState(false);
  const [showDeletedRecords, setShowDeletedRecords] = useState(false);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [view, setView] = useState('table');
  const [isLoading, setIsLoading] = useState(false);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const _getList = async (page = 1) => {
    setIsLoading(true);
    setCurrentPage(page);

    const Payload = {
      IPageNo: page,
      IPageRecords: recordsPerPage,
      FilterType: statusFilter,
      SearchKeyword: searchQuery,
      SortColumn: sortBy,
      SortDirection: sortDirection,
    };

    try {
      await dispatch(
        showDeletedRecords
          ? getCancelledImportRateQuoteList({ ...Payload, CompanyId: companyId })
          : getImportRateQuoteList({ ...Payload, compnayId: companyId })
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (searchQuery.length === 0 || searchQuery.length >= 3) _getList(1);
  }, [searchQuery]);

  useEffect(() => { _getList(1); }, [sortBy, statusFilter, sortDirection, recordsPerPage, showDeletedRecords]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const _isDelete = async (remark) => {
    await deleteImportRateQuoteList({
      ImportRateQuoteSno: selectedOrderId?.rateQuoteSno || 0,
      CancelBy: authName,
      RateQuoteCancelRemarks: remark,
    }).then(() => _getList(currentPage))
      .catch((e) => dispatch(showToast('error', e?.message || t('oceanImport.importRateQuote.list.failedDelete'))))
      .finally(() => setShowDeleteModal(false));
  };

  const _isRestoreDeleted = async (remark) => {
    await reinstateImportRateQuoteList({
      ImportRateQuoteSno: selectedOrderId?.rateQuoteSno || 0,
      ModifyBy: authName,
      RateQuoteReinstateRemarks: remark,
    }).then(() => _getList(currentPage))
      .catch((e) => dispatch(showToast('error', e?.message || t('oceanImport.importRateQuote.list.failedRestore'))))
      .finally(() => setShowDeleteRestoreModal(false));
  };

  const handleCopyQuote = async (rateQuoteSno) => {
    // console.log("handleCopyQuote called with:", rateQuoteSno); // ADD THIS
    const confirmCopy = window.confirm("Are you sure you want to copy this Rate Quote?");
    if (!confirmCopy) return;

    // console.log("confirmed, fetching data..."); // ADD THIS
    try {

      const resultData = await dispatch(getImportRateQuoteByID(rateQuoteSno));
      // console.log("resultData:", resultData); // ADD THIS
      dispatch(callLoader(1));

      if (!resultData) {
        dispatch(showToast("Could not fetch quote data", "error"));
        return;
      }

      const CarrierDetails = (resultData?.CarrierDetails || []).map((carrier) => ({
        ...carrier,
        carrierDetailId: 0,
        rateCharges: (carrier.rateCharges || [])
          .filter((c) => c.chargedescription?.trim())
          .map((c) => ({ ...c, rateid: 0, ratequotesno: 0 })),
        rateQuoteContainer: (carrier.rateQuoteContainer || []).map((c) => ({ ...c, containerId: 0 })),
        ratequoteVessel: (carrier.ratequoteVessel || []).map((v) => ({ ...v, id: 0 })),
      }));

      const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? '[{}]');

      const payload = {
        ...resultData,
        rateQuoteSno: 0,
        effectiveDate: moment().format("YYYY-MM-DD"),
        valideDate: moment().add(30, "days").format("YYYY-MM-DD"),
        IsMultiCarrier: resultData?.IsMultiCarrier || false,
        CarrierDetails,
        cargoUnits: (resultData?.cargoUnits || []).map((u) => ({ ...u, id: 0 })),
        rateQuoteClauses: [],
        companyId: authUser?.companyId,
      };

      // createRateQuote is NOT a thunk — call it directly, no dispatch ✓
      const result = await insertRateQuoteImport(payload);
      // console.log("result", result);
      if (!result) {
        dispatch(showToast(t('oceanImport.importRateQuote.list.failedCopy'), 'error'));
        return;
      }

      dispatch(showToast(t('oceanImport.importRateQuote.list.copySuccess'), 'success', true));
      _getList(currentPage);

    } catch (e) {
      dispatch(showToast("Something went wrong", "error"));
      console.error("Error occurred while copying rate quote:", e);
    } finally {
      dispatch(callLoader(0));
    }

  };

  // ── KPI Cards ─────────────────────────────────────────────────────────────
  const insightCards = useMemo(() => [
    { icon: FileText, title: t('totalQuotes'), value: summary?.total ?? 0, trend: t('fromLastMonthTrend'), trendUp: true, accentKey: 'blue' },
    { icon: Activity, title: t('active'), value: summary?.active ?? 0, trend: t('readyToConvert'), trendUp: true, accentKey: 'emerald' },
    { icon: AlertCircle, title: t('expired'), value: summary?.expire ?? 0, trend: t('needsRenewal'), trendUp: false, accentKey: 'red' },
    { icon: TrendingUp, title: t('conversionRate'), value: `${summary?.conversionRate ?? 0}%`, trend: t('improvementTrend'), trendUp: true, accentKey: 'amber' },
  ], [summary, t]);

  const statusOptions = [
    { value: 'ALL', label: t('all') },
    { value: 'ACTIVE', label: t('active') },
    { value: 'EXPIRED', label: t('expired') },
  ];

  const hasActiveFilters =
    searchQuery !== '' ||
    statusFilter !== 'ALL' ||
    sortBy !== 'recent' ||
    sortDirection !== 'desc';

  const resetFilters = () => {
    setSearchQuery('');
    setStatusFilter('ALL');
    setSortBy('recent');
    setSortDirection('desc');
  };

  const quoteSearchInputClass =
    'w-full pl-10 pr-9 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all';

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-full space-y-3 p-4">

      {/* ════════════════════════════════════════════════════
          HEADER CARD  
      ════════════════════════════════════════════════════ */}
      <div className="rounded-2xl overflow-hidden
                      border border-gray-200 dark:border-gray-800
                      bg-white dark:bg-gray-900
                      shadow-sm dark:shadow-gray-900/40">

        {/* ── Top bar ─────────────────────────────────────── */}
        <div className="px-4 sm:px-5 py-4
                          flex flex-wrap items-center justify-between gap-3
                          border-b border-gray-100 dark:border-gray-800">

          {/* Left */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl
                            bg-blue-50 dark:bg-blue-900/40
                            ring-1 ring-blue-100 dark:ring-blue-800
                            flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <AnimatedTitle />
              <p className="mt-0.5 text-[12px] text-gray-500 dark:text-gray-400
                            hidden sm:block truncate max-w-sm lg:max-w-lg">
                {t('oceanImport.importRateQuote.list.description')}
              </p>
            </div>
          </div>

          {/* Right — actions */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* New Import Shipment */}
            <button
              onClick={() => navigate('/import-booking/')}
              className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg
                         bg-white dark:bg-gray-800
                         border border-gray-200 dark:border-gray-700
                         text-gray-700 dark:text-gray-300
                         hover:bg-gray-50 hover:border-gray-300
                         dark:hover:bg-gray-700/70 dark:hover:border-gray-600
                         text-[12.5px] font-semibold transition-colors"
            >
              <BookmarkPlus className="w-3.5 h-3.5" />
              {/* <span className="hidden xs:inline">{t('newImportShipment')}</span> */}
              <span>{t('oceanImport.importRateQuote.list.newImportShipment')}</span>
            </button>

            {/* New Import Rate Quote */}
            <button
              onClick={() => navigate('/import-rate-quote/new')}
              className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg
                         bg-blue-600 hover:bg-blue-700 active:bg-blue-800
                         dark:bg-blue-500 dark:hover:bg-blue-600
                         text-white text-[12.5px] font-semibold
                         shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)]
                         dark:shadow-[0_4px_14px_-4px_rgba(59,130,246,0.35)]
                         transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
          
              <span>{t('oceanImport.importRateQuote.list.newImportRateQuote')}</span>
            </button>

            <ViewToggle view={view} setView={setView} />
          </div>
        </div>

        {/* ── KPI Cards ───────────────────────────────────── */}
        <div className="px-4 sm:px-5 pt-4 pb-3
                        grid grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3">
          {insightCards.map((card) => (
            <AIInsightCard key={card.title} {...card} />
          ))}
        </div>

        {/* ── AI Insight Banner ────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mx-4 sm:mx-5 mb-4 relative overflow-hidden
                     flex flex-col sm:flex-row items-start sm:items-center gap-3
                     rounded-xl px-4 py-3
                     bg-gradient-to-r from-violet-50 to-indigo-50
                     dark:from-violet-950/50 dark:to-indigo-950/50
                     border border-violet-100 dark:border-violet-900/60"
        >
          <span aria-hidden
            className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl
                       bg-gradient-to-b from-violet-500 via-fuchsia-500 to-pink-500"
          />
          <div className="shrink-0 w-9 h-9 rounded-lg ml-1
                          bg-gradient-to-br from-violet-500 to-indigo-500
                          flex items-center justify-center
                          shadow-[0_4px_12px_-2px_rgba(139,92,246,0.4)]">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em]
                            text-violet-600 dark:text-violet-400">
              AI Insight
            </div>
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-700 dark:text-gray-300">
              {t('rateQuoteAIBannerDescription')}
            </p>
          </div>
          <button
            onClick={() => setShowAIInsights(true)}
            className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg
                       text-[12px] font-semibold
                       text-violet-700 dark:text-violet-300
                       bg-violet-100 dark:bg-violet-900/50
                       hover:bg-violet-200 dark:hover:bg-violet-900
                       ring-1 ring-violet-200 dark:ring-violet-800
                       transition-colors"
            data-testid="view-ai-insights-btn"
          >
            {t('viewDetails')} <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </motion.div>
      </div>
      {/* ════ END HEADER CARD ════ */}

      {/* ── Filter Bar ──────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800
                      bg-white dark:bg-gray-900 shadow-sm px-4 sm:px-5 py-4 space-y-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <SearchInputWithClear
            placeholder={t('searchQuotePlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery('')}
            iconLeft="left-4"
            inputClassName={quoteSearchInputClass}
          />

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Status tabs */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
              {statusOptions.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setStatusFilter(value)}
                  className={`px-3 py-1.5 rounded-lg text-[12.5px] font-medium transition-all ${statusFilter === value
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* More filters dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center gap-2 px-3 py-2
                                   bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700
                                   text-gray-700 dark:text-gray-300 rounded-xl text-[12.5px] font-medium transition-all">
                  <Filter className="w-4 h-4" />
                  {['ALL', 'ACTIVE', 'EXPIRED'].includes(statusFilter) ? t('moreFilters') : statusFilter}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuPortal>
                <DropdownMenuContent className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 min-w-[180px] z-50">
                  {['RATEQUOTENUMBER', 'CUSTOMERNAME', 'CARRIERNAME', 'POL', 'POD', 'ORIGIN', 'DESTINATION'].map((s) => (
                    <DropdownMenuItem key={s}
                      className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                      onSelect={() => setStatusFilter(s)}>
                      {s}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenuPortal>
            </DropdownMenu>

            {/* Sort */}
            <div className="flex items-center gap-1.5">
              <button onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                {sortDirection === 'asc'
                  ? <ArrowUpAZ className="w-4 h-4 text-gray-500" />
                  : <ArrowDownZA className="w-4 h-4 text-gray-500" />}
              </button>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700
                           rounded-xl text-[12.5px] text-gray-700 dark:text-gray-300
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="recent">{t('mostRecent')}</option>
                <option value="expiry">{t('expiryDate')}</option>
                <option value="customer">{t('customerName')}</option>
              </select>
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-[12.5px] font-medium transition-all"
              >
                <RotateCcw className="w-4 h-4" />
                {t('reset')}
              </button>
            )}
          </div>
        </div>

        {/* Results summary + deleted toggle */}
        <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <p className="text-[12.5px] text-gray-500 dark:text-gray-400">
            {showDeletedRecords
              ? t('showingDeletedQuotes', { count: rateQuoteList.length })
              : t('showingOfQuotes', { shown: pagination?.currentRecord || 0, total: pagination?.totalRecord })}
          </p>
          <div className="flex items-center gap-3">
            <span className="text-[12.5px] text-gray-500 dark:text-gray-400">{t('deletedQuotes')}</span>
            <button
              type="button"
              onClick={() => setShowDeletedRecords((p) => !p)}
              className={`relative inline-flex h-5 w-10 shrink-0 items-center rounded-full transition-colors
                          focus:outline-none focus:ring-2 focus:ring-blue-400
                          ${showDeletedRecords ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-700'}`}
              aria-pressed={showDeletedRecords}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform
                               ${showDeletedRecords ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────── */}
      <div className="p-1">
        {rateQuoteList.length === 0 ? (
          <div className="text-center py-20
                          bg-white dark:bg-gray-900
                          rounded-2xl border border-gray-200 dark:border-gray-800">
            <DollarSign className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-300 font-semibold text-lg">No import rate quotes found</p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">{t('oceanImport.importRateQuote.list.emptyDescription')}</p>
            <button
              onClick={() => navigate('/import-rate-quote/new')}
              className="mt-4 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors"
            >
              + {t('oceanImport.importRateQuote.list.newImportRateQuote')}
            </button>
          </div>
        ) : view === 'card' ? (
          /* Card grid */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rateQuoteList.map((quote, i) => (
              <QuoteCard
                key={quote?.rateQuoteSno ?? i}
                quote={quote}
                basePath='import-rate-quote'
                showDeletedRecords={showDeletedRecords}
                navigate={navigate}
                t={t}
                onOpenFileUpload={() => { setSelectedOrderId(quote); setFileUploadVisible(true); }}
                onOpenNotes={() => { setSelectedOrderId(quote); setShowRateQuoteNote(true); }}
                onDelete={() => { setSelectedOrderId(quote); setShowDeleteModal(true); }}
                onRestore={() => { setSelectedOrderId(quote); setShowDeleteRestoreModal(true); }}
                onCopy={handleCopyQuote}
              />
            ))}
          </div>
        ) : (
          /* List/table view */
          <div className="space-y-4">
            <AnimatePresence>
              {rateQuoteList.map((quote, i) => (
                <QuoteCard
                  key={quote?.rateQuoteSno ?? i}
                  quote={quote}
                  basePath='import-rate-quote'
                  showDeletedRecords={showDeletedRecords}
                  navigate={navigate}
                  t={t}
                  onOpenFileUpload={() => { setSelectedOrderId(quote); setFileUploadVisible(true); }}
                  onOpenNotes={() => { setSelectedOrderId(quote); setShowRateQuoteNote(true); }}
                  onDelete={() => { setSelectedOrderId(quote); setShowDeleteModal(true); }}
                  onRestore={() => { setSelectedOrderId(quote); setShowDeleteRestoreModal(true); }}
                  onCopy={handleCopyQuote}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !showDeletedRecords && pagination?.totalPage > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-800
                          pl-2 pr-16 pt-4 pb-8 mb-4 mt-8
                          bg-gray-50 dark:bg-gray-800/50
                          flex items-center justify-between rounded-b-xl">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span>{t('show')}</span>
              <select
                value={recordsPerPage}
                onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
                           rounded-lg text-sm text-gray-700 dark:text-gray-300
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>{t('entries')}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => currentPage > 1 && _getList(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg
                           hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
              >
                {t('previous')}
              </button>
              <button className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg">
                {currentPage}
              </button>
              <button
                onClick={() => pagination?.nextPage > 0 && _getList(pagination.nextPage)}
                disabled={pagination?.nextPage === 0}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg
                           hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
              >
                {t('next')}
              </button>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('showingOfLoadPlans', { shown: pagination?.currentRecord || 0, total: pagination?.totalRecord || 0 })}
            </p>
          </div>
        )}
      </div>

      {/* ── Modals ──────────────────────────────────────────── */}
      <AnimatePresence>
        {showAIInsights && (
          <AIInsightsModal
            onClose={() => setShowAIInsights(false)}
            insightCards={insightCards}
          />
        )}
      </AnimatePresence>

      {fileUploadVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 w-[70%] p-6 rounded-xl shadow-xl">
            <FileUpload orderId={selectedOrderId?.rateQuoteSno} orderNum={selectedOrderId?.rateQuoteNumber} type="importRateQuote" />
            <div className="flex justify-end mt-6">
              <button
                onClick={() => { setFileUploadVisible(false); setSelectedOrderId(null); }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRateQuoteNote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 w-[70%] p-6 rounded-xl shadow-xl">
            <RateQuoteNote
              orderId={selectedOrderId?.rateQuoteSno}
              orderNum={selectedOrderId?.rateQuoteNumber}
              onClose={() => setShowRateQuoteNote(false)}
            />
          </div>
        </div>
      )}

      <RateQuoteCancelModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={_isDelete}
      />

      <RateQuoteCancelRestoreModal
        open={showDeleteRestoreModal}
        onClose={() => setShowDeleteRestoreModal(false)}
        title={t('restoreRateQuote')}
        description={t('restoreRateQuoteDescription')}
        confirmLabel={t('restore')}
        cancelLabel={t('cancel')}
        variant="warning"
        onDelete={_isRestoreDeleted}
      />
    </div>
  );
};

export default ImportRateQuoteList;